import { Error } from './error'
import { Comment } from './comment'
import { Summary } from './summary'

export enum Srp {
  OFF_SRP = 0,
  OPEN_SRP = 1,
  CLOSE_SRP = 2,
}

export enum Reason {
  READABLE = 0, // 閲覧可能
  CAN_NOT_READABLE = 1, // 閲覧不可。非会員・フリー会員
  STILL_NOT_READABLE = 2, // 閲覧不可。シルバー会員
}

export enum CourseId {
  anonymous = 0,
  free = 1,
  silver = 2,
  gold = 3,
}

export enum PayType {
  ZEUS = 0,
  YAHOO = 1,
  IAP_APPLE = 2,
  AUSP = 3,
  SUGOTOKU = 4,
  DOCOMO = 5,
  PAYPAY = 6,
  GOOGLE = 7,
}

export enum WithdrawType {
  CANCEL = 0,
  WITHDRAW = 1,
}

interface Course {
  id: number
  name: string
  price: string
}

interface Plan {
  id: number
  name: string
  restrict_upgrade: number
  withdraw: string
}

export interface Pickup {
  id?: number
  title?: string
  image?: string
  comment_info?: Comment
}

/** アカウント設定の「性別」「居住地」「職業」「職種」「お気に入りのカテゴリ」で利用する共通の型 */
export interface AccountBaseType {
  id: number
  name: string
}

export interface UserInfo {
  result?: boolean
  error?: Error
  user_id?: number
  biz_group_id?: number
  labo_id?: number
  email?: string
  name?: string
  srp_id?: string
  birth?: number
  gender?: AccountBaseType
  pref?: AccountBaseType
  job?: AccountBaseType
  role?: AccountBaseType
  course?: Omit<Course, 'price'>
  favorites?: string
  font_size?: number
  notification?: boolean
  introduction?: string
  pickup?: Pickup
  image?: string
  is_opening?: boolean
  can_search?: boolean
  is_confirmed?: boolean
  is_configured?: boolean
  pay_type?: number
  token?: string
  plan?: Plan
  magazine1?: number
  use_srp?: boolean
  use_school?: boolean
  biz_group_use_srp?: number
  biz_group_name?: string
  is_public_stand?: boolean
  is_public_reading_data?: boolean
  next_paydate?: string
  code?: number
  message?: number
}

export interface GetCheckResponse {
  api_key: string
  api_secret: string
  notification: number
  result: boolean
  type: boolean // 施設型の場合true
}

export interface GetCheckFacilityResponse {
  result: boolean
  api_key?: string
  api_secret?: string
}

export interface PostLoginResponse {
  result: boolean
  access_token?: string
  refresh_token?: string
  api_key?: string
  api_secret?: string
  error?: string
}

export interface PostGetSessionResponse {
  result: boolean
  user?: {
    api_key: string
    api_secret: string
    biz_group_id: string
    biz_group_name: string
    biz_use_srp: string
    course: Course
    email: string
    id: string
    labo_id: string
    name: string
    notices: number
    pay_type: string
    plan_id: string
    read_this_month: string[]
    restrict_upgrade: number
    sendid: string
    srp_id: string
    summaries: Record<number, string>
    use_school: string
    use_srp: string
    withdraw: string
  }
  access_token?: string
  refresh_token?: string
}

export interface BookPercent {
  summary_id: number
  percent: number
}

export interface GetBookPercentResponse {
  result: boolean
  book_percents: BookPercent[]
}

export interface Member {
  id: number
  name: string
  srp_id: string
  image: string
  introduction: string
  is_followed: boolean
  is_following: boolean
  is_requesting: boolean
  is_opening: boolean
  is_muting: boolean
  is_blocking: boolean
}

export interface GetFollowResponse {
  result: boolean
  members?: Member[]
  total?: number
  error?: Error
}

export interface PostFollowResponse {
  result: boolean
  requested: boolean
}

export interface DeleteFollowResponse {
  result: boolean
}

export interface PostFollowCancelResponse {
  result: boolean
}

export interface PostForgotpwdResponse {
  result: boolean
}

export interface GetUserCheckReminderResponse {
  result: boolean
  user?: UserInfo
}

export interface PostResetpwdResponse {
  result: boolean
  api_key?: string
  api_secret?: string
  access_token?: string
  refresh_token?: string
  error?: Error
}

export interface PostWithdrawResponse {
  result: boolean
  csrfToken?: string
  reasons?: Record<
    '01' | '02' | '03' | '04' | '05' | '06' | '07' | '08' | '09' | '99',
    string
  >
  reads?: number
  readings?: number
}

export interface GetUserStandResponse {
  result: boolean
  total?: number
  summaries?: {
    id: number
    title: string
    subtitle: string
    subpobooleans: number
    free: boolean
    ja: boolean
    en: boolean
    new: boolean
    percent: string
    image: string
  }[]
  is_public_stand?: boolean
  error?: Error
}

export interface GetUserPrefResponse {
  result: boolean
  prefs: AccountBaseType[]
  error?: Error
}

export interface GetUserJobResponse {
  result: boolean
  jobs: AccountBaseType[]
  error?: Error
}

export interface GetUserRoleResponse {
  result: boolean
  roles: AccountBaseType[]
  error?: Error
}

export interface GetUserFavoriteResponse {
  result: boolean
  favorites: AccountBaseType[]
  error?: Error
}

export interface PostUserInfoResponse {
  result: boolean
  token?: string
  error?: Error
}

export interface PostUserInfoRequest {
  introduction: string
  pickupId: number
  isOpening: boolean
  canSearch: boolean
  isConfigured?: boolean
  isConfirmed?: boolean
  isPublicStand: boolean
  isPublicReadingData: boolean
  email: string
  password: string
  name: string
  srpId: string
  birth: number
  genderId: number
  prefId: number
  jobId: number
  roleId: number
  favorites: string
  magazine1: number
  token: string
  csrfToken: string
}

export const isAccountBaseTypeList = (
  // eslint-disable-next-line
  item: any[]
): item is AccountBaseType[] => {
  return item !== undefined && item[0]?.id && item[0]?.name
}

export interface GetUserHeaderResponse {
  result: boolean
  error?: Error
  user_id?: number
  name?: string
  srp_id?: string
  course?: number
  image?: string
  plan?: number
  biz_group_id?: number
  labo_id?: number
  monthly_read?: number
  sum_read?: number
  follow?: number
  follower?: number
}

export interface GetCheckValidationResponse {
  result: boolean
}

export interface PostEditPickupResponse {
  result: boolean
  error?: Error
}

export enum NotificationType {
  // FOLLOW_REQUEST = 1, // フォローリクエスト ※ 利用されていない
  FOLLOW_ACCEPT = 2, // フォローリクエスト許可
  NICE = 3, // 自分のコメントに対するなるほど通知
  // COMMENT_OPEN = 4, // 学びメモの公開通知 ※ 利用されていない
  RECOMMEND = 5, // おすすめ
  // RECOMMEND_REACTION = 6, // おすすめに対するいいね ※ 利用されていない
  FOLLOWED = 7, // フォローされました
}

interface NotificationRecommend {
  recommend_id: number
  summary_id: number
  title: string
  comment: string
  summary_image: string
}

export interface Notification {
  notification_id: number
  type: NotificationType
  member: Member
  comment: Comment
  recommend: NotificationRecommend
  is_appreciating: boolean
  elapsed: string
}

export interface NotificationFollowRequest {
  name: string
  image_first: string
  image_second: string
  request_count: number
}

export interface GetUserNotificationResponse {
  result: boolean
  error?: Error
  new?: number
  notification?: Notification[]
  follow_request?: NotificationFollowRequest
  biz_notification?: {
    notification_name: string
    unread_count: number
    new_count: number
  }
  total?: number
}

export interface BizNotification {
  name: string
  body: string
  summary: Summary
  user_notice_id: number
  referred: string
}

export interface GetUserBizNotificationResponse {
  result: boolean
  error?: Error
  total?: number
  notification?: {
    biz_notification: BizNotification
    created: string
    elapsed: string
  }[]
}

export interface PostUserBizNotificationResponse {
  result: boolean
  error?: Error
}

export interface PostUserFollowAcceptResponse {
  result: boolean
  error?: Error
}

export interface PostUserFollowRejectResponse {
  result: boolean
  error?: Error
}

interface FollowRequestMember extends Omit<Member, 'id'> {
  follow: number
  follower: number
  monthly_read: number
  sum_read: number
  user_id: number
}

interface FollowRequest {
  member: FollowRequestMember
  created: string
  elapsed: string
}

export interface GetUserFollowRequestResponse {
  result: boolean
  error?: Error
  total?: number
  requests?: FollowRequest[]
}

export interface GetUserHistoryResponse {
  result: boolean
  error?: Error
  free?: {
    read: { summaries: Summary[] }
    reading: { summaries: Summary[] }
  }
  paid?: {
    read: { summaries: Summary[] }
    reading: { summaries: Summary[] }
  }
}

export interface PostUserHistoryResponse {
  result: boolean
  error?: Error
}

export interface PostUserProfileImageResponse {
  result: boolean
}

export interface PostUserRegistRequest {
  email: string
  password: string
  name: string
  birth: number
  genderId: number
  prefId: number
  jobId: number
  roleId: number
  fontSize: number
  favorites: string
  referrer: string
}

export interface PostUserRegistResponse {
  result: boolean
  error?: string
  user?: {
    id?: number
    access_token?: string
    refresh_token?: string
    api_key?: string
    api_secret?: string
    expires?: string
    notification?: boolean
  }
}
